import { asList } from '../lib/api';
import { useState, useEffect, useCallback, useRef } from 'react';
import { formatDate } from '../lib/dates';
import { downloadsApi, downloadFile, type SchoolDocument } from '../lib/downloadsApi';

/**
 * What a school actually publishes to parents.
 *
 * ⚠ THERE IS NO "GENERAL". A bin category is where everything ends up — it is always the shortest
 * word on the list and it is pre-selected — and once half the files are in it the category has
 * stopped sorting anything. "Other" is last and deliberately awkward.
 *
 * ⚠ "Notice" AND "Circular" WERE BOTH ON THE LIST and mean the same thing in an Indian school; two
 * words for one idea guarantees the same kind of file lands under both. Circular is what the office
 * calls it, so that is the one that survived.
 *
 * The rest come from what these schools send home: the fee structure, the exam timetable, the
 * holiday list, the book and uniform list before the year starts, the bus routes, and the leave,
 * bonafide and transfer-certificate forms.
 *
 * ⚠ Free text on the wire — the column is an 80-character string with no server-side whitelist, so
 * changing this list does not invalidate anything already uploaded. Old rows keep the word they
 * were saved with and still display; they simply will not match one of the options below.
 */
const CATEGORIES = [
  'Circular',
  'Syllabus',
  'Exam timetable',
  'Calendar & holidays',
  'Fee structure',
  'Forms',
  'Book & uniform list',
  'Transport',
  'Results',
  'Other',
] as const;

/** Admin page to publish downloadable files for families (the Download List). */
export function DownloadsAdminPage() {
  const [rows, setRows] = useState<SchoolDocument[]>([]);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<string>('Circular');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(() => { downloadsApi.list().then((r) => setRows(asList(r))).catch(() => setRows([])); }, []);
  useEffect(load, [load]);

  const upload = async () => {
    if (!file) { setMsg('Choose a file.'); return; }
    setBusy(true); setMsg(null);
    try {
      await downloadsApi.upload(file, title.trim() || file.name, category, null);
      setTitle(''); setFile(null); if (fileInput.current) fileInput.current.value = '';
      load();
    } catch { setMsg('Upload failed.'); } finally { setBusy(false); }
  };

  const fmtSize = (b: number) => b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`;

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Files</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Files for families</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4 }}>
          Circulars, the fee structure, timetables, forms — anything a parent should be able to
          fetch for themselves. Published under <strong>My family → Downloads</strong>.
        </p>
      </div>

      <div className="card" style={{ padding: 18, marginBottom: 16, maxWidth: 620 }}>
        <div style={{ fontWeight: 600, marginBottom: 12 }}>Upload a file</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <input className="input" placeholder="Title — leave blank to use the file name" value={title} onChange={(e) => setTitle(e.target.value)} style={{ flex: '2 1 220px' }} />
          <select className="input" value={category} onChange={(e) => setCategory(e.target.value)} style={{ width: 'auto' }}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <input ref={fileInput} type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} style={{ marginBottom: 6, display: 'block' }} />
        {/* ⚠ The native control says "No file chosen" and then the file's own name in the browser's
            wording, which is easy to miss beside a Title box somebody has just typed into. Saying
            what will actually be published removes the "did it take my file?" question. */}
        {/* ⚠ Only once a file is chosen. The native control already prints "No file chosen" two
            lines above, and repeating it in different words reads as a second, contradictory
            control rather than as confirmation. */}
        {file && (
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 12 }}>
            Publishing <strong>{file.name}</strong> as{' '}
            <strong>{title.trim() || file.name}</strong> under {category}.
          </div>
        )}
        <button className="btn btn-accent" disabled={busy} onClick={upload}>{busy ? 'Uploading…' : 'Upload'}</button>
        {msg && <span style={{ marginLeft: 12, fontSize: 13, color: 'var(--ink-2)' }}>{msg}</span>}
      </div>

      {rows.length === 0
        ? <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)' }}>No files yet.</div>
        : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rows.map((d) => (
              <div key={d.id} className="card" style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 220px' }}>
                  <div style={{ fontWeight: 600 }}>{d.title} <span className="pill" style={{ fontSize: 11 }}>{d.category}</span></div>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{d.fileName} · {fmtSize(d.sizeBytes)} · {formatDate(d.createdAt)}</div>
                </div>
                <button className="btn" onClick={() => downloadFile('admin', d.id, d.fileName)}>Download</button>
                <button className="btn" onClick={async () => { await downloadsApi.setPublished(d.id, !d.isPublished); load(); }}>
                  {d.isPublished ? 'Unpublish' : 'Publish'}
                </button>
                <button className="btn" onClick={async () => { if (window.confirm('Delete this file?')) { await downloadsApi.remove(d.id); load(); } }}>✕</button>
              </div>
            ))}
          </div>}
    </div>
  );
}
